// ЭКРАН ШАГА 4 — слоистая карта в кадре покоя. B6-3 `3e619875cbb881f5a852c4ff770513ee`.
//
// ИСТОЧНИК — борд `Life Map - s4 Layered Map v10.html` (sha256 `ed131e8b…e9a5`), разметка `view()`
// `:489–500`, чарт `chartHTML()` `:384–402`, рельс `railHTML()` `:403–408`, подсказка `applySel()`
// `:553–567`, блок находки `findingHTML()` `:457–467`. Где борд и спека s4 расходятся — права спека
// (`s743.d1`); копи — нормативная таблица слотов спеки, раздел «Копи-слоты v10».
//
// ⛔ ЧЕГО ЗДЕСЬ НЕТ, И У КАЖДОГО ПУНКТА СВОЙ ТИКЕТ:
//   · ⭐ REVEAL — B6-4 (`34a19875cbb881659219fa582fad0f62`): силуэт средних, три волны по `WAVES`,
//     подпись волны живыми слоями без имени семьи, `skip`, reduced motion → сразу покой. Расписание
//     и подписи — `s4Reveal.js`; раскрытие линии от среднего — `layerGeom(…, unfold)`. Один раз за
//     сессию — токен `'s4'` в `revealsPlayed` (B6-6, `s956.d2` п. 2). Приглушённая CTA — B6-7;
//   · силуэта на пустом этапе — тикет `3c919875cbb881d6ba18e861b14c797f`.
//
// ⭐ ШТОРКИ (B6-5, `s956.d1`): срез этапа живёт АДРЕСОМ `#s4/N` (этап с единицы) — открывает колонка,
// «назад» браузера закрывает, перезагрузка открывает снова, прямой вход до reveal — сначала кадр
// покоя. Шторка слоя — вне истории, как листы `?` шага 3: открывают `?` подсказки и тап по имени
// нетронутого слоя. Копи, вью-модели и разбор адреса — `s4Sheet.js`; поведение — по образцу шторки
// шага 5 (B7-5): экран под шторкой `inert`, подвал молчит. Общего компонента с шагом 5 нет намеренно:
// вид у шторок разный (у шага 5 цитата и курсив несущих, у шага 4 — строка источника), а связь через
// общий файл заставила бы правку одного шага задевать приёмку другого.
//
// ⛔ СНЕСЕНО СПЕКОЙ И НЕ ПЕЧАТАЕТСЯ ВОВСЕ (`s586.d1`, `s615.d2`): хвост подсказки о пунктире, ветка
// solo о неотвеченных этапах, счётчик дыр на рельсе, мягкий путь источников. Сторожит
// `src/render/__parity__/wizard-s4-board.test.js` — этот файл стоит в его `S4_SURFACE`.
//
// ⭐ ФУТЕР — ОБОЛОЧКИ. Пара `← back` / `continue` приходит из `footerNav.js`; у борда здесь
// `find the pattern` и `← back to the cards` (`:497–498`). Решения против таблицы нет, поэтому экран
// печатает код, а расхождение — строка в реестре отставаний `WIZARD-CANON.md` (DoR s947, п.5.3).

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { HANDLED, useWizard } from '../WizardProvider.jsx';
import { parseAddress } from '../address.js';
import { toWizardInput } from '../toWizardInput.js';
import { buildPassport } from '../../render/passportData.js';
import { viewIV } from '../../render/viewFromPassport.js';
import { ROMAN } from '../../roman.js';
import { findingBlock, EVIDENCE_LABEL } from './stepFourFinding.js';
import {
  CH, PAN_FROM, S4_LAYERS, SELECTED_EXTRA_W,
  bandPath, isLive, layerGeom, layerSeries, s4Geometry, silhouettePath, stageMeans, viewMode, yV, yearTicks,
} from './s4Chart.js';
import {
  RV4, RV4_END, SKIP_LABEL, captionAt, rememberReveal, revealPlayed, waveOf, waveProgress,
} from './s4Reveal.js';
import { prefersReducedMotion } from './s5Reveal.js';
import {
  LAYER_OFF, S4_ADDRESS, S4_SHEET_CLOSE, cutAddressS4, cutSheetView, cutWhereS4, layerSheetView,
} from './s4Sheet.js';

// ---- копи экрана: слоты спеки s4, байт борда v10 ----------------------------------------------

/** `s4.head.lede` (борд `:491`): курсивная строка с акцентом на последнем слове. */
export const S4_LEDE = { lead: 'this is the same life, ', em: 'unfolded.' };
/** `s4.head.count` (борд `:490`): `N stages · 7 layers`. */
export const s4Count = (n) => `${n} stages · `;
export const S4_LAYER_COUNT = '7 layers';
/** `s4.legend.mode` (борд `:404`). */
export const modeLabel = (mode) => `mode · ${mode}`;
/** `s4.legend.zoom` (борд `:404`): тумблер протяжки при N ≥ 9. */
export const zoomLabel = (pan, n) => (pan ? `fit all ${n} ⤢` : 'close up ⤢');
/** `s4.legend.pan` (борд `:402`). */
export const PAN_HINT = 'pan →';
/** `s4.rail.untouched` (борд `:408`, первая ветка) — единственная подпись рядом с именем слоя. Дом
 * строки — `s4Sheet.js` (её же печатает шторка нетронутого слоя, `s620.d3`). */
export const RAIL_UNTOUCHED = LAYER_OFF;
/** `s4.hint.constellation` (борд `:560`) — законченная фраза, хвоста о пунктире нет (`s586.d1`). */
export const HINT_IDLE = 'tap layer names to compare · tap a stage column to open it';
/** `s4.hint.compare` (борд `:561`). */
export const HINT_COMPARE = 'the shaded band is the distance between them';
/** `s4.hint.solo` (борд `:563`) — только ветка без дыр: ветка о неотвеченных снесена (`s586.d1`). */
export const hintSolo = (name) => `what ${name} means · add names to compare`;
/** Сброс выбора (`#rel`, борд `:561`/`:563`). */
export const CLEAR_LABEL = 'clear';
/** `s4.finding.raillabel` (борд `:461`) — режим `carriers`; тумблер `named` запаркован (`s447.d1`). */
export const FINDING_RAIL = 'carriers';
/** `s4.finding.suffix` (борд `:465`) — в режиме `carriers` хвост строки доказательств. */
export const FINDING_SUFFIX = 'named on the next step';
/** `s4.finding.lift` (борд `:607`) — надпись поднятого состояния. */
export const RELEASE_LABEL = 'release';

// ---- данные ----------------------------------------------------------------------------------

/**
 * Прогон движка по конверту. Допуск (`stepAdmission.js`) пускает на шаг 4 только выровненный и
 * прошедший гейты ввод, поэтому бросок стыка здесь — порча конверта, а не состояние экрана.
 */
function useStepFourData(session) {
  const { stages, sliders } = session.input;
  const { nowY } = session;
  return useMemo(() => {
    const input = toWizardInput({ rows: stages }, { vals: sliders }, { nowY });
    const passport = buildPassport(input);
    const { mainPattern, redLine } = passport.system1;
    const byKey = Object.fromEntries(S4_LAYERS.map((L) => [L.key, L]));
    return {
      series: S4_LAYERS.map((_, li) => layerSeries(sliders, li)),
      years: input.wizard.stages.map((s) => s.endY),
      // Для шторки среза: ряды ползунков в порядке контракта и имена/даты этапов — из того же вью,
      // что печатает даты на шаге 5 и в §IV (`mmYYYY`), а не своим форматом.
      sliders,
      stages: viewIV(passport).stages || [],
      // Несущие — `mainPattern.layers` движка, в его порядке (борд `:433–438`, спека: не корреляция).
      carriers: mainPattern.layers.map((k) => S4_LAYERS.indexOf(byKey[k])).filter((li) => li >= 0),
      finding: findingBlock({
        view: redLine,
        names: mainPattern.layers.map((k) => byKey[k]?.name).filter(Boolean),
        evidenceStages: passport.carrierStages.indices,
      }),
    };
  }, [stages, sliders, nowY]);
}

// ---- часы reveal -------------------------------------------------------------------------------

/**
 * Часы reveal шага 4: мс от монтирования, фаза и видимость находки.
 *
 * Решение «играть или нет» принимается на монтировании: reduced motion — сразу покой, находка
 * сразу (`settle(true)` борда). Иначе ход кадров — `requestAnimationFrame` (линии меняют
 * геометрию каждый кадр, CSS-переходом это не выразить), конец — по последней волне (`RV4_END`)
 * или страховкой `RV4.SAFETY` (`RVTO` `:527`), если кадры не дошли (вкладка в фоне).
 *
 * ⭐ ДВА ВИДА ВХОДА В ПОКОЙ, КАК У БОРДА: естественный конец и `skip` — `settle()`, находка
 * проявляется через `FIND_LAG`; страховка, тап по слою и reduced motion — `settle(true)`, сразу.
 *
 * ⭐ `played` — СЫГРАН ЛИ REVEAL В ЭТОЙ СЕССИИ (токен `'s4'`, B6-6). Читается ОДИН РАЗ, на
 * монтировании, как у шага 5: токен пишется по ходу, и чтение на каждом рендере погасило бы reveal
 * его же записью. Сыгран — кадр покоя как при reduced motion: без силуэта, подписей и `skip`.
 *
 * @param {boolean} [played]
 */
export function useS4RevealClock(played = false) {
  const [state, setState] = useState(() => (played || prefersReducedMotion()
    ? { phase: 'rest', now: Infinity, findOn: true, played: false, natural: false }
    : { phase: 'reveal', now: 0, findOn: false, played: true, natural: false }));
  const lagRef = useRef(null);

  // `natural` — покой пришёл сам: конец кадров или страховка (B6-7, `s977.d2` п. 3 — только тогда
  // кнопка выходит из приглушения плавно). `skip` и тап по колонке зовут `settle` без него.
  const settle = useCallback((quiet, natural = false) => {
    setState((s) => (s.phase === 'rest' ? s : { ...s, phase: 'rest', now: Infinity, findOn: quiet, natural }));
    if (!quiet && lagRef.current === null) {
      lagRef.current = setTimeout(() => setState((s) => ({ ...s, findOn: true })), RV4.FIND_LAG);
    }
  }, []);

  const startedLive = useRef(state.phase === 'reveal');
  useEffect(() => {
    if (!startedLive.current) return undefined;
    const raf = typeof requestAnimationFrame === 'function'
      ? requestAnimationFrame
      : (fn) => setTimeout(() => fn(performance.now()), 16);
    const caf = typeof cancelAnimationFrame === 'function' ? cancelAnimationFrame : clearTimeout;
    const t0 = performance.now();
    let id = null;
    let alive = true;
    const step = () => {
      if (!alive) return;
      const now = performance.now() - t0;
      if (now >= RV4_END) { settle(false, true); return; }
      setState((s) => (s.phase === 'reveal' ? { ...s, now } : s));
      id = raf(step);
    };
    id = raf(step);
    const safety = setTimeout(() => settle(true, true), RV4.SAFETY);
    return () => { alive = false; caf(id); clearTimeout(safety); };
  }, [settle]);

  useEffect(() => () => { if (lagRef.current !== null) clearTimeout(lagRef.current); }, []);

  return { ...state, settle };
}

// ---- экран -----------------------------------------------------------------------------------

export default function StepFour() {
  const { session, update, publishGate, view: address, goTo, router } = useWizard();
  const data = useStepFourData(session);
  const where = cutWhereS4(parseAddress(address), data.series[0].length);
  // ⭐ ВХОД ПРЯМО НА `#s4/N` (ссылка, перезагрузка) — кадр покоя с первого рендера, затем шторка
  // (`s956.d1`): часы решают «играть или нет» один раз на монтировании, и открытая шторка значит «не
  // играть». Токен `'s4'` пишет тот же эффект входа в покой ниже.
  const clock = useS4RevealClock(revealPlayed(session.revealsPlayed) || where.open !== null);
  const live = clock.phase === 'reveal';

  // Недопустимый адрес шторки — ПОДМЕНА записи на `#s4` (`s956.d1`): «назад» не должен вернуть на
  // адрес, который экран только что поправил.
  useEffect(() => {
    if (where.redirect) goTo(where.redirect, { replace: true });
  }, [where.redirect, goTo]);

  // Шторка слоя — вне истории (локальное состояние). `gen` — поколение открытия, как у листа `?`
  // шага 3 (Н36): закрытие старого кадра не гасит новый.
  const [layerSheet, setLayerSheet] = useState(null);
  const gen = useRef(0);
  const sheetOpen = where.open !== null || layerSheet !== null;

  // Тап по колонке. Посреди reveal — сперва покой (`settle(true)` борда `:600`; токен пишет эффект
  // входа в покой), потом шторка. Новая запись истории: «назад» её снимет. Шторка уже открыта —
  // второй записи поверх нет (`inert` экрана программный клик не глушит, замер Н37).
  const openCut = useCallback((i) => {
    if (sheetOpen) return;
    if (live) clock.settle(true);
    goTo(cutAddressS4(i));
  }, [sheetOpen, live, clock, goTo]);
  // Закрытие — «назад», если запись своя, иначе подмена на `#s4` (вход ссылкой или перезагрузкой).
  const closeCut = useCallback(() => {
    if (!router.back()) goTo(S4_ADDRESS, { replace: true });
  }, [router, goTo]);

  const openLayer = useCallback((li) => {
    if (sheetOpen) return;
    gen.current += 1;
    setLayerSheet({ li, gen: gen.current });
  }, [sheetOpen]);

  const remember = useCallback(() => update((s) => {
    const played = rememberReveal(s.revealsPlayed);
    return played === s.revealsPlayed ? s : { ...s, revealsPlayed: played };
  }), [update]);

  // ⭐ ТОКЕН ПИШЕТСЯ НА ВХОДЕ В ПОКОЙ (B6-6, `s956.d2` п. 2 по образцу `s952.d2`): конец, `skip`, тап
  // посреди reveal, страховка, reduced motion — все они ведут в `phase: 'rest'`. На уже сыгранном
  // входе запись — no-op (прежний массив по ссылке).
  useEffect(() => {
    if (!live) remember();
  }, [live, remember]);

  // Уход со шага посреди reveal пишет токен тем же нажатием (`s952.d2` п. 3 у шага 5): человек видел
  // экран, и «назад» с шага 5 — сразу покой. Надписи и вид кнопок — таблицы оболочки, гейт их не
  // трогает; обработчики живут только на время reveal.
  // ⭐ ШТОРКА ОТКРЫТА — ПОДВАЛ МОЛЧИТ (как лист `?` шага 3 и шторка шага 5): вуаль накрывает его
  // глазу, но клавиатура дотянется — `HANDLED` без перехода значит «нажатие принято, идти некуда».
  // ⭐ ПРИГЛУШЁННАЯ CTA (B6-7, `s977.d2` п. 1): бледная, пока график не сел, нажимается всё время.
  // Выход из приглушения плавный только на естественной посадке (`fade`), иначе сразу.
  // ⭐ LAYOUT-ЭФФЕКТ: вид кнопки обязан встать до первой отрисовки шага — иначе на входе виден
  // прежний вид и переход из него (дефект шага 5, `s977.d2` п. 4; здесь — тот же механизм).
  useLayoutEffect(() => {
    if (sheetOpen) publishGate({ onPress: () => HANDLED, onBack: () => false, muted: live });
    else publishGate(live ? { onPress: remember, onBack: remember, muted: true } : { fade: clock.natural });
  }, [sheetOpen, live, remember, publishGate, clock.natural]);
  useEffect(() => () => publishGate(null), [publishGate]);

  return (
    <>
      <StepFourScreen data={data} clock={clock} onCut={openCut} onLayer={openLayer} inert={sheetOpen} />
      <CutSheetS4 data={data} open={where.open} onClose={closeCut} />
      {layerSheet === null ? null : (
        <LayerSheetS4
          key={layerSheet.gen}
          view={layerSheetView(layerSheet.li, data.series[layerSheet.li])}
          onClose={() => setLayerSheet((cur) => (cur && cur.gen === layerSheet.gen ? null : cur))}
        />
      )}
    </>
  );
}

/** Ход шторки — `transform .3s` (борд `.sheet` `:131`); уход узлов — `280` мс (`kill()` `:584`). */
export const SHEET_MS = 280;

/** Рамка шторки (борд `openSheet()` `:579–585`): вуаль, шит, два уголка, тело, `got it`. */
function SheetFrame({ kind, on, onClose, closeRef, stage, children }) {
  return (
    <>
      <div data-testid="s4-veil" className={`s4-veil${on ? ' on' : ''}`} onClick={onClose} />
      <div data-testid="s4-sheet" data-kind={kind} {...(stage === undefined ? {} : { 'data-stage': stage })}
        className={`s4-sheet${on ? ' on' : ''}`} role="dialog" aria-modal="true">
        <span className="cor tl" /><span className="cor tr" />
        {children}
        <button ref={closeRef} type="button" data-testid="s4-sheet-close" onClick={onClose}>{S4_SHEET_CLOSE}</button>
      </div>
    </>
  );
}

/** Escape закрывает, пока шторка открыта (у борда только тап; как листы `?` шага 3 и шторка шага 5). */
function useEscape(active, onClose) {
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, onClose]);
}

/**
 * Шторка слоя (борд `sheetLayer()` `:586–589`). Вне истории: монтируется по тапу, `.on` кадром позже,
 * закрытие — снять `.on` и через `SHEET_MS` сообщить родителю (как `SheetFrame` шага 3).
 */
export function LayerSheetS4({ view, onClose }) {
  const [on, setOn] = useState(false);
  const closeRef = useRef(null);
  useEffect(() => {
    const id = requestAnimationFrame(() => setOn(true));
    // `preventScroll` — фокус на кнопке не должен двигать полосу шага (урок листа `?` шага 3).
    if (closeRef.current) closeRef.current.focus({ preventScroll: true });
    return () => cancelAnimationFrame(id);
  }, []);
  const timer = useRef(null);
  const leave = useCallback(() => {
    setOn(false);
    clearTimeout(timer.current);
    timer.current = setTimeout(onClose, SHEET_MS);
  }, [onClose]);
  useEffect(() => () => clearTimeout(timer.current), []);
  useEscape(true, leave);
  return (
    <SheetFrame kind="layer" on={on} onClose={leave} closeRef={closeRef}>
      <span data-testid="s4-sheet-hd" className="smcaps">{view.head}</span>
      <h4>{view.name}</h4>
      {view.gloss ? <p><b>{view.gloss.lead}</b>{` ${view.gloss.body}`}</p> : null}
      {view.off === null ? null : <p data-testid="s4-sheet-off" className="off"><b>{view.off}</b></p>}
    </SheetFrame>
  );
}

/**
 * Шторка среза этапа (борд `sheetStage()` `:590–596`). Открыта она или нет, решает АДРЕС (`open`):
 * «назад» браузера закрывает её тем же путём, что и кнопка. Своё состояние одно — дожить ход после
 * закрытия, чтобы уехать вниз, а не исчезнуть (образец `CutSheet` шага 5).
 */
export function CutSheetS4({ data, open, onClose }) {
  const [last, setLast] = useState(open);
  const [gone, setGone] = useState(open === null);
  const [entered, setEntered] = useState(false);
  if (open !== null && open !== last) setLast(open);
  if (open !== null && gone) setGone(false);
  if (open === null && entered) setEntered(false);
  const shown = gone ? null : (open ?? last);
  const on = entered && open !== null;
  const closeRef = useRef(null);

  useEffect(() => {
    if (open !== null) {
      const id = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(id);
    }
    const id = setTimeout(() => setGone(true), SHEET_MS);
    return () => clearTimeout(id);
  }, [open]);
  useEffect(() => {
    if (open !== null && closeRef.current) closeRef.current.focus({ preventScroll: true });
  }, [open, shown]);
  useEscape(open !== null, onClose);

  if (shown === null || shown >= data.series[0].length) return null;
  const cut = cutSheetView(shown, data.sliders, data.stages);
  return (
    <SheetFrame kind="cut" stage={cut.i} on={on} onClose={onClose} closeRef={closeRef}>
      <span data-testid="s4-sheet-hd" className="smcaps">{cut.head}</span>
      <h4>{cut.name}</h4>
      <div className="dt">{cut.dt}</div>
      <div className="cut">
        {cut.rows.map((r) => (
          <div key={r.key} data-testid={`s4-crow-${r.key}`} className={`crow${r.v === null ? ' nil' : ''}`}>
            <span className="nm">{r.name}</span>
            <span className="bar">
              <span className="base" /><span className="mid" />
              {r.v === null ? null : <span className="fl" style={{ left: `${r.left}%`, width: `${r.w}%`, background: r.color }} />}
            </span>
            <span className="pw">{r.pole}</span>
          </div>
        ))}
      </div>
      <div data-testid="s4-sheet-src" className="src">{cut.source}</div>
    </SheetFrame>
  );
}

/** Разметка экрана. Экспортирована ради тестов reveal с подставными часами. */
export function StepFourScreen({ data, clock, onCut, onLayer, inert = false }) {
  const { series, years, carriers, finding } = data;
  const n = series[0].length;
  const live = clock.phase === 'reveal';

  // Выбор слоёв и протяжка — локальный вид, в конверт не идут (спека: «View-контролы, НЕ контракт»).
  const [selected, setSelected] = useState([]);
  const [pan, setPan] = useState(false);

  const geom = s4Geometry(n, { pan });
  const mode = viewMode(selected);
  const lifted = !finding.scatter && carriers.length > 0
    && selected.length === carriers.length && carriers.every((li) => selected.includes(li));

  const toggle = (li) => {
    if (live) clock.settle(true); // тап посреди reveal — сначала покой (`:599`)
    // Нетронутый слой — шторка слоя, а не выбор (`:598`).
    if (!isLive(series[li])) { if (onLayer) onLayer(li); return; }
    setSelected((sel) => (sel.includes(li) ? sel.filter((x) => x !== li) : [...sel, li]));
  };
  // Тап по блоку находки поднимает несущие, повторный отпускает (`:603–608`). При scatter поднимать
  // нечего — у вью-модели нет подписи `lift`, и блок тогда не контрол вовсе (`Finding`, `s493.d2`
  // вариант b). ⚠️ Второй проверки scatter здесь нет намеренно: у scatter `mainPattern.layers` —
  // ВСЕ тронутые слои (`scatterPattern`), и решение «поднимать ли» живёт в одном месте.
  const liftCarriers = () => setSelected(lifted ? [] : carriers.slice());

  return (
    // `inert` на время шторки (`aria-modal` его не заменяет: без него Shift+Tab уводит с `close` на
    // экран). Атрибут строкой, как у шагов 2 и 5: React 18 булева не знает.
    <div data-testid="s4-screen" className="s4-screen" data-mode={mode} inert={inert ? '' : undefined}>
      <p data-testid="s4-count" className="s4-count">
        {s4Count(n)}<b>{S4_LAYER_COUNT}</b>
      </p>
      {/* `skip` — только в фазе анимации (`:499`), ведёт в покой (`settle()` `:601`). Обёртка
          нулевой высоты, как у шага 5: кнопка ложится поверх правого края строки заголовка (как
          у борда, `top:47px`) и раскладку не двигает; ниже, над чартом, она закрыла бы римскую цифру. */}
      {live ? (
        <div className="s4-skipwrap">
          <button type="button" data-testid="s4-skip" className="s4-skip" onClick={() => clock.settle(false)}>{SKIP_LABEL}</button>
        </div>
      ) : null}
      <h2 data-testid="s4-lede" className="s4-title">
        {S4_LEDE.lead}<em>{S4_LEDE.em}</em>
      </h2>

      <Chart geom={geom} series={series} years={years} selected={selected} clock={clock} onCut={onCut} />

      {/* Полоса подписи reveal (`.rvcap` `:493`): вводная, затем волны. В покое пуста — и как у
          борда (`:not(:empty)`) не занимает места; здесь узла в покое нет вовсе. */}
      {live ? <RevealCaption now={clock.now} series={series} /> : null}

      <div className="s4-railhd">
        <span data-testid="s4-mode" className="md">{modeLabel(mode)}</span>
        <span className="ln" />
        {n >= PAN_FROM ? (
          <button type="button" data-testid="s4-zoom" className="s4-zb" onClick={() => setPan((p) => !p)}>
            {zoomLabel(pan, n)}
          </button>
        ) : null}
      </div>
      <div data-testid="s4-rail" className="s4-rail">
        {S4_LAYERS.map((L, li) => {
          const live = isLive(series[li]);
          const on = selected.includes(li);
          return (
            <button
              key={L.key}
              type="button"
              data-testid={`s4-layer-${li}`}
              data-layer={L.key}
              aria-pressed={on}
              className={`s4-lrow${live ? '' : ' off'}${on ? ' on' : ''}`}
              onClick={() => toggle(li)}
            >
              <span
                className="sw"
                style={{
                  borderTopColor: L.color,
                  borderTopStyle: L.dash ? (L.dash === '1.5 4' ? 'dotted' : 'dashed') : 'solid',
                }}
              />
              <span className="nm">{L.name}</span>
              {live ? null : <span className="gp">{RAIL_UNTOUCHED}</span>}
            </button>
          );
        })}
      </div>

      <div className="s4-read">
        {/* Подсказка молчит на время reveal (`:520`) и возвращается в покое (`:537`). */}
        <div data-testid="s4-hint" className="s4-hint" style={live ? { opacity: 0 } : undefined}>
          {selected.length === 1 ? (
            // `?` — вход в шторку слоя (`s4.sheet.*`, борд `:565`).
            <button type="button" data-testid="s4-help" className="help" aria-label={hintSolo(S4_LAYERS[selected[0]].name)}
              onClick={onLayer ? () => onLayer(selected[0]) : undefined}>?</button>
          ) : null}
          <span>
            {selected.length === 0 ? HINT_IDLE : null}
            {selected.length === 1 ? hintSolo(S4_LAYERS[selected[0]].name) : null}
            {selected.length > 1 ? HINT_COMPARE : null}
          </span>
          {selected.length > 0 ? (
            <button type="button" data-testid="s4-clear" className="s4-rel" onClick={() => setSelected([])}>
              {CLEAR_LABEL}
            </button>
          ) : null}
        </div>
      </div>

      <Finding finding={finding} lifted={lifted} onLift={liftCarriers} on={clock.findOn} />
    </div>
  );
}

/**
 * Подпись reveal: вводная курсивом до первой волны, затем имена нарисованных слоёв волны
 * (`s4.reveal.wave`, `s620.d2`). Имени семьи нет (`s493.d3`).
 */
function RevealCaption({ now, series }) {
  const drawn = series.map(isLive);
  const cap = captionAt(now, S4_LAYERS, drawn);
  return (
    <div data-testid="s4-rvcap" className="s4-rvcap">
      {cap.intro ? <i>{cap.text}</i> : <b>{cap.text}</b>}
    </div>
  );
}

function Chart({ geom, series, years, selected, clock, onCut }) {
  const { n, W, top, bot } = geom;
  const live = clock.phase === 'reveal';
  const act = selected.length > 0;
  const solo = selected.length === 1 && isLive(series[selected[0]]) ? selected[0] : null;
  const band = act ? bandPath(selected.map((li) => series[li]), geom) : '';
  // Reveal: каждый слой едет от силуэта средних к своим значениям по ходу своей волны.
  const means = live || clock.played ? stageMeans(series) : null;
  const tOf = (li) => (live ? waveProgress(waveOf(S4_LAYERS[li].key), clock.now) : 1);
  const geoms = series.map((s, li) => (live && means !== null
    ? layerGeom(s, geom, { t: tOf(li), means })
    : layerGeom(s, geom)));
  // Силуэт виден до `SIL_OUT` и гаснет за `.9s` (`:519`, `:524`); в покое — ноль.
  const sil = means === null ? '' : silhouettePath(means, geom);
  const silOn = live && clock.now < RV4.SIL_OUT;
  const silStyle = { transition: 'opacity .9s ease-out' };
  return (
    <div className="s4-cwrap">
      <span className="cor tl" /><span className="cor tr" /><span className="cor bl" /><span className="cor br" />
      <div className={`s4-cscroll${geom.pannable ? '' : ' nofit'}`}>
        <svg
          data-testid="s4-chart"
          viewBox={`0 0 ${W} ${CH}`}
          preserveAspectRatio="none"
          style={{ width: geom.pannable ? `${W}px` : '100%' }}
        >
          <defs>
            <filter id="s4-lg" x="-25%" y="-25%" width="150%" height="150%"><feGaussianBlur stdDeviation="2.4" /></filter>
          </defs>
          <line x1="0" y1={yV(0)} x2={W} y2={yV(0)} stroke="rgba(143,136,122,.2)" strokeDasharray="2 6" strokeWidth=".7" />
          {Array.from({ length: n - 1 }, (_, i) => (
            <line key={`sep${i}`} x1={geom.xEdge(i + 1).toFixed(1)} y1={top - 10} x2={geom.xEdge(i + 1).toFixed(1)} y2={(bot + 10).toFixed(1)} stroke="#1C1E22" strokeWidth=".7" />
          ))}
          <rect
            data-testid="s4-nowband"
            x={geom.xEdge(n - 1).toFixed(1)}
            y={top - 10}
            width={(geom.xEdge(n) - geom.xEdge(n - 1)).toFixed(1)}
            height={(bot - top + 20).toFixed(1)}
            fill="rgba(212,138,60,.05)"
          />
          {clock.played ? (
            <>
              <path data-testid="s4-sil" d={sil} fill="none" stroke="var(--amber)" strokeWidth="2.6" strokeLinecap="round" opacity={silOn ? 0.55 : 0} style={silStyle} />
              <path data-testid="s4-silglow" d={sil} fill="none" stroke="var(--amber)" strokeWidth="7" strokeLinecap="round" opacity={silOn ? 0.16 : 0} filter="url(#s4-lg)" style={silStyle} />
            </>
          ) : null}
          <path data-testid="s4-band" d={band} fill="rgba(212,138,60,.1)" stroke="rgba(212,138,60,.3)" strokeWidth=".6" opacity={band ? 1 : 0} />
          <path
            data-testid="s4-glow"
            d={solo === null ? '' : geoms[solo].d}
            fill="none"
            stroke={solo === null ? S4_LAYERS[0].color : S4_LAYERS[solo].color}
            strokeWidth="5"
            strokeLinecap="round"
            opacity={solo === null ? 0 : 0.3}
            filter="url(#s4-lg)"
          />
          {S4_LAYERS.map((L, li) => {
            const g = geoms[li];
            if (!g) return null; // слой не тронут вовсе — линии нет (`s601.d1`)
            const on = selected.includes(li);
            // Во время reveal слой невидим, пока его волна не тронулась (`paint()` `:505`).
            const hidden = live && tOf(li) <= 0;
            return (
              <path
                key={L.key}
                data-testid={`s4-line-${li}`}
                {...(hidden ? { opacity: 0 } : {})}
                data-layer={L.key}
                className={`s4-lay${act && !on ? ' dim' : ''}`}
                d={g.d}
                fill="none"
                stroke={L.color}
                strokeWidth={on ? +(L.w + SELECTED_EXTRA_W).toFixed(1) : L.w}
                strokeDasharray={L.dash ?? undefined}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            );
          })}
          {S4_LAYERS.map((L, li) => {
            const g = geoms[li];
            if (!g || g.rings.length === 0) return null;
            return (
              <g key={L.key} data-testid={`s4-rings-${li}`} className={`s4-mk${act && !selected.includes(li) ? ' dim' : ''}`} {...(live && tOf(li) <= 0 ? { opacity: 0 } : {})}>
                {g.rings.map((r) => (
                  <circle key={r.x} cx={r.x.toFixed(1)} cy={r.y.toFixed(1)} r="2.6" fill="none" stroke={L.color} strokeWidth="1.2" />
                ))}
              </g>
            );
          })}
          {/* Колонки этапов — цель тапа в срез этапа (`#cols rect`, `:399`, `:600`). */}
          <g>
            {Array.from({ length: n }, (_, i) => (
              <rect
                key={`col${i}`}
                data-testid={`s4-col-${i}`}
                x={geom.xEdge(i).toFixed(1)}
                y={top - 10}
                width={(geom.xEdge(i + 1) - geom.xEdge(i)).toFixed(1)}
                height={(bot - top + 20).toFixed(1)}
                fill="transparent"
                onClick={onCut ? () => onCut(i) : undefined}
              />
            ))}
          </g>
          {Array.from({ length: n }, (_, i) => (
            <g key={`stg${i}`}>
              <text className={`s4-stg${i === n - 1 ? ' cur' : ''}`} x={geom.xMid(i).toFixed(1)} y={top - 16} textAnchor="middle">{ROMAN[i]}</text>
              <line className={`s4-stgtick${i === n - 1 ? ' cur' : ''}`} x1={(geom.xMid(i) - 5).toFixed(1)} y1={top - 11} x2={(geom.xMid(i) + 5).toFixed(1)} y2={top - 11} />
            </g>
          ))}
          {yearTicks(years, geom).map((t) => (
            <text key={t.label + t.x} className={`s4-yr${t.now ? ' now' : ''}`} x={t.x.toFixed(1)} y={(bot + 22).toFixed(1)} textAnchor="middle">{t.label}</text>
          ))}
        </svg>
      </div>
      {geom.pannable ? <div data-testid="s4-pan" className="s4-pan"><span>{PAN_HINT}</span></div> : null}
    </div>
  );
}

/**
 * Блок находки. Значения — только из вью-модели `stepFourFinding.js` (B6-2): новой логики находки
 * здесь нет. Цвет один при любом состоянии (`s617.d2`): отсутствие несущей — ответ, а не сбой.
 */
function Finding({ finding, lifted, onLift, on = true }) {
  const line = finding.predicate === null
    ? `${finding.subject}.`
    : `${finding.subject}${finding.separator}${finding.predicate}`;
  const stages = finding.evidence === null ? null : finding.evidence.slice(EVIDENCE_LABEL.length).trim();
  // Блок — контрол только когда есть что поднимать. У борда это `div` с `onclick` (`:602`); здесь та
  // же цель, но с ролью и клавишами, иначе подъём был бы доступен только мыши.
  const liftable = finding.lift !== null;
  const press = (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onLift(); }
  };
  return (
    <div
      data-testid="s4-finding"
      data-scatter={finding.scatter ? 'true' : 'false'}
      className={`s4-find${on ? ' on' : ''}${lifted ? ' lifted' : ''}`}
      {...(liftable ? { role: 'button', tabIndex: 0, 'aria-pressed': lifted, onClick: onLift, onKeyDown: press } : {})}
    >
      <div className="rail-lb">{FINDING_RAIL}</div>
      <div className="bd">
        <div data-testid="s4-finding-eyebrow" className="eyb">{finding.eyebrow}</div>
        {finding.carriers === null ? null : <div data-testid="s4-finding-names" className="nm">{finding.carriers}</div>}
        <p data-testid="s4-finding-line">{line}</p>
        {stages === null ? null : (
          <div data-testid="s4-finding-evidence" className="ev">
            {EVIDENCE_LABEL} <b>{stages}</b> · <span className="sfx">{FINDING_SUFFIX}</span>
          </div>
        )}
        {finding.lift === null ? null : (
          <div data-testid="s4-finding-lift" className="lift">{lifted ? RELEASE_LABEL : finding.lift}</div>
        )}
      </div>
    </div>
  );
}
